/*
"Suggestion" card on the map screen — surfaces the next tree the
user should head toward, computed by `computeDiscoverSuggestion`
(closest never-seen species, fallback to closest under-explored
species).

DOM lifecycle: the card lives inside the map screen's root, written
once at mount; its inner content is rewritten per refresh. Tap on
the card → callback (the map screen flies the camera + opens the
bottom sheet for that tree). The × dismisses for the rest of the
session — the card reappears on the next mount or after the user
makes any capture.
*/

import type { DiscoverSuggestion } from '../../data/discoverSuggestion'

const kCardId: string = 'discover-suggestion-card'

/*
Renders the card's static markup once into the parent. Visibility
and content are driven by `updateSuggestionCard` — call that with a
fresh suggestion to show or null to hide. Returns the DOM ref so
callers don't have to re-query.
*/
export function renderSuggestionCardShell(): string
{
	return `
		<aside class="suggestion-card" id="${kCardId}" data-open="false" role="status">
			<div class="suggestion-card-body">
				<span class="suggestion-card-kind" id="suggestion-kind">Suggestion</span>
				<span class="suggestion-card-primary" id="suggestion-primary"></span>
				<span class="suggestion-card-secondary" id="suggestion-secondary"></span>
				<span class="suggestion-card-distance" id="suggestion-distance"></span>
			</div>
			<button class="suggestion-card-close" id="suggestion-close" type="button" aria-label="Masquer">×</button>
		</aside>
	`
}

/*
Update / show / hide the card. Pass a non-null suggestion to paint
+ reveal; pass null to hide. The dismissed flag (per-session
in-memory, cleared by the caller when state changes meaningfully)
is honoured by the caller — this function does not enforce it.
*/
export function updateSuggestionCard(inRoot: HTMLElement, inSuggestion: DiscoverSuggestion | null): void
{
	const vCard = inRoot.querySelector<HTMLElement>(`#${kCardId}`)
	if (!vCard) return
	if (!inSuggestion)
	{
		vCard.setAttribute('data-open', 'false')
		return
	}
	const vKind = inRoot.querySelector<HTMLElement>('#suggestion-kind')
	const vPrimary = inRoot.querySelector<HTMLElement>('#suggestion-primary')
	const vSecondary = inRoot.querySelector<HTMLElement>('#suggestion-secondary')
	const vDistance = inRoot.querySelector<HTMLElement>('#suggestion-distance')
	if (!vKind || !vPrimary || !vSecondary || !vDistance) return

	if (inSuggestion.kind === 'new-species')
	{
		vKind.textContent = '✨ Nouvelle espèce à découvrir'
		vCard.dataset.kind = 'new-species'
	}
	else
	{
		vKind.textContent = '🔁 Variante à explorer'
		vCard.dataset.kind = 'variety'
	}
	vPrimary.textContent = inSuggestion.primaryLabel
	vSecondary.textContent = inSuggestion.secondaryLabel
	vSecondary.style.display = inSuggestion.secondaryLabel.length > 0 ? '' : 'none'
	vDistance.textContent = `à ${formatDistance(inSuggestion.distanceMeters)}`
	vCard.setAttribute('data-open', 'true')
}

function formatDistance(inMetres: number): string
{
	if (inMetres < 950) return `${Math.round(inMetres / 10) * 10} m`
	return `${(inMetres / 1000).toFixed(1)} km`
}
